import React from 'react';
import { Bolt, Clock } from 'lucide-react';
import type { NotificationSetting, SendCountSummary } from '@/services/notificationSettingsService';
import { ReviewRequestBackfillPanel } from './ReviewRequestBackfillPanel';

interface AdvancedTabProps {
  setting: NotificationSetting;
  countSummary?: SendCountSummary;
  countsError?: string | null;
}

function triggerTag(setting: NotificationSetting): string {
  const category = setting.category?.replace(/[_-]/g, ' ') || 'Automatic';
  if (category.toLowerCase().includes('appraisal')) {
    return 'Document Upload';
  }
  if (category.toLowerCase().includes('compensation')) {
    return 'Status Change';
  }
  if (category.toLowerCase().includes('disclosure')) {
    return 'Field Populated';
  }
  return category;
}

function formatLastSent(value: string | null | undefined): string {
  if (!value) {
    return 'No sends found';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

export function AdvancedTab({ setting, countSummary, countsError }: AdvancedTabProps) {
  return (
    <div className="en-advanced-stack">
      <section className="en-section">
        <div className="en-section-title">Send Condition</div>
        <div className="en-trigger-card">
          <div className="en-trigger-icon"><Bolt /></div>
          <div className="min-w-0 flex-1">
            <div className="en-trigger-meta">{triggerTag(setting)}</div>
            <div className="en-trigger-text">{setting.trigger_description}</div>
            <span className="en-trigger-cadence"><Clock className="h-3 w-3" /> Immediate</span>
          </div>
        </div>
      </section>

      <section className="en-section">
        <div className="en-section-title">Operational Context</div>
        <div className="en-stats">
          <div className="en-stat-card">
            <div className="en-stat-label">All-time sent</div>
            <div className="en-stat-value">{countsError ? '-' : countSummary?.countAllTime ?? countSummary?.count ?? 0}</div>
            <div className="en-stat-sub">sent attempts</div>
          </div>
          <div className="en-stat-card">
            <div className="en-stat-label">Sent (30d)</div>
            <div className="en-stat-value">{countsError ? '-' : countSummary?.count ?? 0}</div>
            <div className="en-stat-sub">{countsError ? 'unavailable' : 'recent activity'}</div>
          </div>
          <div className="en-stat-card">
            <div className="en-stat-label">Last sent</div>
            <div className="en-stat-value small">{countsError ? 'Unavailable' : formatLastSent(countSummary?.lastSent)}</div>
            <div className="en-stat-sub">{setting.is_enabled ? 'active' : 'paused'}</div>
          </div>
        </div>
      </section>

      {setting.notification_type === 'google_review_request' && (
        <section className="en-section">
          <div className="en-section-title">Backfill</div>
          <ReviewRequestBackfillPanel />
        </section>
      )}
    </div>
  );
}
